<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  currentThreadId: {
    type: String,
    default: null,
  },
  threads: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'newThread',
  'switchThread',
  'deleteThread',
  'toggleConfig',
  'refresh',
  'reset',
  'close',
]);

const { t } = useI18n();
const isOpen = ref(false);
const dropdownRef = ref(null);

const currentThreadTitle = computed(() => {
  if (!props.currentThreadId) return t('AGENTS_SIDECAR.THREADS.NEW_CHAT');
  const currentThread = props.threads.find(
    thread => thread.id === props.currentThreadId
  );
  return currentThread?.title || t('AGENTS_SIDECAR.THREADS.CHAT');
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = event => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

const handleNewThread = () => {
  emit('newThread');
  isOpen.value = false;
};

const handleSwitchThread = threadId => {
  emit('switchThread', threadId);
  isOpen.value = false;
};

const handleDeleteThread = (e, threadId) => {
  e.stopPropagation();
  emit('deleteThread', threadId);
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});
</script>

<template>
  <div
    class="flex items-center justify-between w-full h-14 px-4 bg-n-solid-1 border-b border-n-weak"
  >
    <div class="flex items-center gap-2 min-w-0 flex-1">
      <div
        class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
      >
        <span class="i-lucide-sparkles text-n-brand text-lg" />
        {{ t('AGENTS_SIDECAR.TITLE') }}
      </div>
      <div class="h-4 w-px bg-n-weak mx-1" />
      <div ref="dropdownRef" class="relative min-w-0 flex-1">
        <button
          class="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 rounded-md transition-colors max-w-full"
          @click.stop="toggleDropdown"
        >
          <span class="truncate">{{ currentThreadTitle }}</span>
          <span
            class="i-lucide-chevron-down text-xs text-n-slate-9 transition-transform duration-200 flex-shrink-0"
            :class="{ 'rotate-180': isOpen }"
          />
        </button>

        <div
          v-if="isOpen"
          class="absolute top-full left-0 mt-1 w-64 bg-n-solid-1 border border-n-weak rounded-lg shadow-xl z-50 py-1 max-h-[400px] flex flex-col animate-fade-in-up"
        >
          <div class="p-2 border-b border-n-weak">
            <button
              class="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-n-brand hover:bg-n-brand/10 rounded-md transition-colors"
              @click="handleNewThread"
            >
              <span class="i-lucide-plus-circle text-base" />
              {{ $t('AGENTS_SIDECAR.THREADS.NEW_CHAT') }}
            </button>
          </div>

          <div class="overflow-y-auto flex-1 p-1">
            <div
              v-if="!threads.length"
              class="px-4 py-6 text-center text-sm text-n-slate-9 italic"
            >
              {{ $t('AGENTS_SIDECAR.THREADS.NO_THREADS') }}
            </div>
            <button
              v-for="thread in threads"
              :key="thread.id"
              class="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-md transition-colors group"
              :class="{
                'bg-n-brand/5 text-n-brand font-medium':
                  thread.id === currentThreadId,
              }"
              @click="handleSwitchThread(thread.id)"
            >
              <div class="flex items-center gap-3 overflow-hidden">
                <span
                  class="flex-shrink-0 text-base"
                  :class="
                    thread.id === currentThreadId
                      ? 'i-lucide-message-circle'
                      : 'i-lucide-message-square'
                  "
                />
                <span class="truncate">{{ thread.title }}</span>
              </div>
              <button
                class="flex-shrink-0 p-1 rounded hover:bg-r-500/10 text-n-slate-9 hover:text-r-500 opacity-0 group-hover:opacity-100 transition-all"
                @click="handleDeleteThread($event, thread.id)"
              >
                <span class="i-lucide-trash-2 text-xs" />
              </button>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center gap-1">
      <button
        class="p-1.5 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-md transition-colors"
        :title="$t('AGENTS_SIDECAR.ACTIONS.REFRESH')"
        @click="$emit('refresh')"
      >
        <span class="i-lucide-refresh-cw text-sm" />
      </button>
      <button
        class="p-1.5 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-md transition-colors"
        :title="$t('AGENTS_SIDECAR.ACTIONS.RESET')"
        @click="$emit('reset')"
      >
        <span class="i-lucide-trash-2 text-sm" />
      </button>
      <div class="h-4 w-px bg-n-weak mx-1" />
      <button
        class="p-1.5 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-md transition-colors"
        :title="$t('AGENTS_SIDECAR.ACTIONS.CONFIG')"
        @click="$emit('toggleConfig')"
      >
        <span class="i-lucide-settings-2 text-sm" />
      </button>
      <button
        class="p-1.5 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-md transition-colors ml-1"
        @click="$emit('close')"
      >
        <span class="i-lucide-x text-sm" />
      </button>
    </div>
  </div>
</template>
